/**
 * Parwaaz backend — passphrase sessions.
 *
 * The browser derives a key from the staff member's passphrase with PBKDF2 (600k iterations,
 * salt `parwaaz|<user>`) and sends the derived key, never the passphrase, as `derivedKey`
 * (base64url). The server never sees the passphrase: it compares SHA-256(derivedKey) against
 * the stored secret and, on success, issues an HMAC-signed session token good for 8 hours.
 */
import type { LoginRes, Role, Session } from '@parwaaz/shared';
import type { Platform, Store } from '../store';
import { ForbiddenError, RateLimitedError, UnauthorizedError } from './errors';

const SESSION_TTL_MS = 8 * 60 * 60 * 1000;
const LOGIN_MAX_FAILURES = 10;
const LOGIN_WINDOW_MS = 15 * 60 * 1000;

interface LoginFailState {
  count: number;
  until: number; // ms epoch; the window resets after this
}

export function constantTimeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

function loginFailKey(user: string): string {
  return `login_fail_${user}`;
}

function readLoginFailState(store: Store, user: string): LoginFailState | null {
  const raw = store.getConfig(loginFailKey(user));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as LoginFailState;
  } catch {
    return null;
  }
}

function assertNotRateLimited(store: Store, user: string, now: number): void {
  const state = readLoginFailState(store, user);
  if (state && state.until > now && state.count >= LOGIN_MAX_FAILURES) {
    throw new RateLimitedError(`too many failed sign-in attempts for '${user}' — try again later`);
  }
}

function recordLoginFailure(store: Store, user: string, now: number): void {
  const existing = readLoginFailState(store, user);
  const state: LoginFailState =
    existing && existing.until > now ? { count: existing.count + 1, until: existing.until } : { count: 1, until: now + LOGIN_WINDOW_MS };
  store.setConfig(loginFailKey(user), JSON.stringify(state));
}

function clearLoginFailures(store: Store, user: string): void {
  store.setConfig(loginFailKey(user), JSON.stringify({ count: 0, until: 0 }));
}

/** Reads the session-signing secret, minting one with the platform's CSPRNG on first use. */
export function getOrCreateSessionSecret(store: Store, platform: Platform): string {
  const existing = store.getSecret('SESSION_SECRET');
  if (existing) return existing;
  const secret = platform.randomToken(32);
  store.setSecret('SESSION_SECRET', secret);
  return secret;
}

function sign(user: string, role: Role, exp: number, secret: string, platform: Platform): string {
  return platform.hmacSha256B64url(`${user}|${role}|${exp}`, secret);
}

/** Verifies the passphrase, applies the rate limit and issues a session token. */
export function login(store: Store, platform: Platform, user: string, derivedKey: string): LoginRes {
  const now = Date.now();
  assertNotRateLimited(store, user, now);

  const staff = store.getStaff(user);
  const storedHash = store.getStaffSecret(user);
  const candidateHash = platform.sha256B64url(derivedKey);
  const ok = !!staff && staff.active && !!storedHash && constantTimeEqual(candidateHash, storedHash);

  if (!ok) {
    recordLoginFailure(store, user, now);
    throw new UnauthorizedError('unknown user or wrong passphrase');
  }
  clearLoginFailures(store, user);

  const exp = now + SESSION_TTL_MS;
  const secret = getOrCreateSessionSecret(store, platform);
  const token = `${user}|${staff!.role}|${exp}|${sign(user, staff!.role, exp, secret, platform)}`;
  return { token, user, role: staff!.role, displayName: staff!.displayName, exp };
}

/** Verifies a session token's signature, expiry and that the staff member is still active. */
export function verifySession(store: Store, platform: Platform, token: string): Session | null {
  if (!token) return null;
  const parts = token.split('|');
  if (parts.length !== 4) return null;
  const [user, role, expRaw, sig] = parts;
  const exp = Number(expRaw);
  if (!Number.isFinite(exp)) return null;

  const secret = store.getSecret('SESSION_SECRET');
  if (!secret) return null;
  const expected = sign(user, role as Role, exp, secret, platform);
  if (!constantTimeEqual(sig, expected)) return null;
  if (Date.now() > exp) return null;

  const staff = store.getStaff(user);
  if (!staff || !staff.active || staff.role !== role) return null;
  return { user, role: staff.role, displayName: staff.displayName, exp };
}

export function requireRole(session: Session, roles: readonly Role[]): void {
  if (!roles.includes(session.role)) {
    throw new ForbiddenError(`role '${session.role}' is not permitted for this action`);
  }
}
